import { autoinject, bindable } from 'aurelia-framework';
import { DecimalsValueConverter } from 'value-converters/decimals';
import { I18N } from 'aurelia-i18n';
import { PubSub } from 'lib/event';
import { NetsImpregnationDto } from 'models/statistics/nets';

type Row = {
  __yearHeader__?: boolean;
  Year?: number;

  NetType?: string;
  ImpregnationType?: string;
  Count?: number | string;
  AverageAmount?: number | string;
};

@autoinject()
export class NetsImpregnationDataTable {
  @bindable
  data: NetsImpregnationDto[] | null = null;

  protected tableTitle: string = this.t.tr('general.antifouling');

  columns: { key: string; label: string }[] = [
    { key: 'NetType', label: this.t.tr('general.NetType') },
    { key: 'ImpregnationType', label: 'Impregnation Type' },
    { key: 'Count', label: 'Count' },
    { key: 'AverageAmount', label: 'Avg Liters / Service' },
  ];

  rows: Row[] = [];

  constructor(
    private decimalsValueConverter: DecimalsValueConverter,
    private t: I18N,
    private pubsub: PubSub
  ) {
    this.pubsub.sub('i18n:locale:changed', () => this.onLocaleChanged());
  }

  onLocaleChanged() {
    this.tableTitle = this.t.tr('general.antifouling');
    this.columns = [
      { key: 'NetType', label: this.t.tr('general.NetType') },
      { key: 'ImpregnationType', label: 'Impregnation Type' },
      { key: 'Count', label: 'Count' },
      { key: 'AverageAmount', label: 'Avg Liters / Service' },
    ];
    this.createTable();
  }

  dataChanged() {
    this.tableTitle = this.t.tr('general.antifouling');
    this.columns = [
      { key: 'NetType', label: this.t.tr('general.NetType') },
      { key: 'ImpregnationType', label: 'Impregnation Type' },
      { key: 'Count', label: 'Count' },
      { key: 'AverageAmount', label: 'Avg Liters / Service' },
    ];
    this.createTable();
  }

  createTable() {
    const arr = Array.isArray(this.data) ? this.data : [];
    arr.sort((a, b) => (b.Year ?? 0) - (a.Year ?? 0));

    const rows: Row[] = [];

    for (const y of arr) {
      if (!y || !Array.isArray(y.YearlyImpregnationData)) continue;

      // year header row
      rows.push({ __yearHeader__: true, Year: y.Year });

      // for each net type in that year
      const nets = [...y.YearlyImpregnationData].sort((a, b) => String(a.NetType ?? '').localeCompare(String(b.NetType ?? '')));

      for (const net of nets) {
        const idata = Array.isArray(net.ImpregnationData) ? net.ImpregnationData : [];

        for (const imp of idata) {
          rows.push({
            NetType: net.NetType,
            ImpregnationType: imp.ImpregnationType,
            Count: imp.Count ?? '',
            AverageAmount: imp.AverageAmount ?? '',
          });
        }
      }
    }

    this.rows = rows;
  }

  detached() {
    this.pubsub.unsub();
  }

  isYearHeader(row: Row): boolean {
    return !!row.__yearHeader__;
  }
  yearLabel(row: Row): string {
    return `Year: ${row.Year}`;
  }
  cell(row: Row, col: keyof Row) {
    return row[col];
  }
  formatCell(v: string | number | null): string {
    if (v == null) return '';
    if (typeof v === 'number' && Number.isFinite(v)) {
      return this.decimalsValueConverter.toView(v, 2);
    }
    return String(v);
  }
}
